import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TbUsers, TbCash, TbCreditCard, TbShoppingCart } from 'react-icons/tb';
import { RootState } from '../../../redux/reducers';
import { BalanceStats, Currency } from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';
import {
  FinancialTable,
  FinancialDataCell,
} from '../../components/tables/FinancialTable';
import PageHeader from '../../components/misc/PageHeader';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import connectionService from '../../../services/community/connectionService';
import { formatBalanceWithSign } from '../../../utils/bankAccountUtil';
import { checkMandateStatusForUser } from '../../../services/community/merchant.marketplace.service';
import { C2BOrderMetaOutDto } from '../../../models/marketplace.model';
import { getOrdersForUserMetadata } from '../../../services/community/orders.marketplace.service';

type BalanceViewModel = [string, { personal: BalanceStats & { initialBalance: number }; contribution: BalanceStats }];

// GL6572120000072123
interface FinancialMemberBalancePageProps {
  hideHeader?: boolean;
}

export default function FinancialMemberBalancePage({
  hideHeader = false,
}: FinancialMemberBalancePageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.memberBalance' });
  const navigate = useNavigate();
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER,
  );
  const [contributionBalances, setContributionBalances] = React.useState<Record<
    number,
    BalanceStats
  > | null>();
  const [balances, setBalances] = React.useState<BalanceViewModel[]>([]);
  const [mandateStatus, setMandateStatus] = React.useState<Record<number, boolean>>({});
  const [orderMetadata, setOrderMetadata] = React.useState<Record<number, C2BOrderMetaOutDto>>({});
  const [walletBalances, setWalletBalances] = React.useState<Record<number, number>>({});
  const [loading, setLoading] = React.useState(true);
  const [selectedCurrency] = React.useState<Currency>(Currency.EUR); // Default to EUR, could be made selectable
  const community = useSelectedCommunity();

  useEffect(() => {
    if (community) {
      setLoading(true);
      Promise.all([
        bankAccountService.getContributionBalancesForConnections(),
        connectionService.getConnections(true),
      ])
        .then(async ([bs, cs]) => {
          setContributionBalances(bs);
          
          // Fetch wallet balances for each member
          const walletBalancePromises = cs.map(async (c) => {
            try {
              const balance = await bankAccountService.getMemberWalletBalance(
                +c.dashboardMemberId,
                selectedCurrency,
              );
              return { memberId: +c.dashboardMemberId, balance: balance.balance };
            } catch (error) {
              console.error(`Error fetching wallet balance for member ${c.dashboardMemberId}:`, error);
              return { memberId: +c.dashboardMemberId, balance: 0 };
            }
          });
          
          const walletBalancesResult = await Promise.all(walletBalancePromises);
          const walletBalancesMap: Record<number, number> = {};
          walletBalancesResult.forEach(({ memberId, balance }) => {
            walletBalancesMap[memberId] = balance;
          });
          setWalletBalances(walletBalancesMap);
          
          // Check mandate status for each connection
          cs.forEach((c) => {
            checkMandateStatusForUser(c.userId ?? -1)
              .then((status) => {
                setMandateStatus((prev) => ({ ...prev, [c.dashboardMemberId]: status }));
              })
              .catch(() => {
                setMandateStatus((prev) => ({ ...prev, [c.dashboardMemberId]: false }));
              });
            getOrdersForUserMetadata(c.userId ?? -1)
              .then((meta) => {
                setOrderMetadata((prev) => ({ ...prev, [c.dashboardMemberId]: meta }));
              })
              .catch(() => { });
          });
        })
        .catch((error) => {
          console.error('Error loading financial data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [community, selectedCurrency]);

  useEffect(() => {
    if (contributionBalances && Object.keys(walletBalances).length > 0 && connections.length > 0) {
      // Use wallet balances from API instead of calculating from transactions
      const balanceViewModels: BalanceViewModel[] = connections.map((connection) => {
        const memberId = +connection.dashboardMemberId;
        const walletBalance = walletBalances[memberId] ?? 0;
        const initialBalance = connection.financeAdministrationStartAmount ?? 0;
        
        return [
          memberId.toString(),
          {
            personal: {
              expenses: 0, // Not tracked separately in wallet system
              income: 0, // Not tracked separately in wallet system
              balance: walletBalance,
              initialBalance,
            },
            contribution: { ...(contributionBalances[memberId] ?? DEFAULT_BALANCE_STATS) },
          },
        ];
      });
      
      setBalances(balanceViewModels.sort(([a], [b]) => +a - +b));
    }
  }, [contributionBalances, walletBalances, connections]);

  const handleRowClick = useCallback((dashboardMemberId: number) => {
    navigate(`/financial/member/${dashboardMemberId}`);
  }, []);

  const renderRow = useCallback(
    ([dashboardMemberId, memberStats]: BalanceViewModel) => {
      const connection = connections.find((c) => +c.dashboardMemberId === +dashboardMemberId);
      const status = mandateStatus[+dashboardMemberId] ? 'active' : '-';
      const statusBadge = status === 'active' ? (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-success">
          {status}
        </span>
      ) : (
        <span className="text-primary-300">-</span>
      );

      return (
        <>
          <FinancialDataCell onClick={() => handleRowClick(+dashboardMemberId)}>
            {connection?.fields.FIRST_NAME} {connection?.fields.LAST_NAME}
          </FinancialDataCell>
          <FinancialDataCell
            onClick={() => handleRowClick(+dashboardMemberId)}
            className={`text-center font-medium ${memberStats.personal.balance >= 0 ? 'text-success' : 'text-danger'
              }`}>
            {formatBalanceWithSign(memberStats.personal.balance, selectedCurrency, i18n.language)}
          </FinancialDataCell>
          <FinancialDataCell
            onClick={() => handleRowClick(+dashboardMemberId)}
            className="text-center">
            {statusBadge}
          </FinancialDataCell>
          <FinancialDataCell
            onClick={() => handleRowClick(+dashboardMemberId)}
            className="text-center font-medium">
            {orderMetadata[+dashboardMemberId]?.totalOrders ?? '-'}
          </FinancialDataCell>
        </>
      );
    },
    [connections, handleRowClick, mandateStatus, orderMetadata, selectedCurrency, i18n.language],
  );

  // Calculate overall statistics
  const overallStats = React.useMemo(() => {
    if (!balances.length) return null;

    const totalPersonalBalance = balances.reduce(
      (sum, [, stats]) => sum + stats.personal.balance,
      0,
    );
    const totalContributionBalance = balances.reduce(
      (sum, [, stats]) => sum + stats.contribution.balance,
      0,
    );

    // Mock data for direct debit and sales orders
    const totalDirectDebitActive = mandateStatus
      ? Object.values(mandateStatus).filter((status) => status).length
      : 0;
    const totalSalesOrders = Object.values(orderMetadata).reduce(
      (sum, meta) => sum + (meta.totalOrders ?? 0),
      0,
    );
    return {
      totalMembers: balances.length,
      totalPersonalBalance,
      totalContributionBalance,
      totalDirectDebitActive,
      totalSalesOrders,
    };
  }, [balances]);

  return (
    <div className="flex flex-col">
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}
      {loading && <EmptyScreenView title={t('loading') || 'Loading...'} subtitle="" />}

      {!loading && overallStats && (
        <>
          {/* Overall Statistics */}
          <div className="mb-8">
            <h3 className="mb-6 text-lg font-semibold text-primary-900">{t('overallStats.title')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbUsers className="h-6 w-6 text-primary-300" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">
                      {t('overallStats.totalMembers')}
                    </p>
                    <p className="text-xl font-bold text-primary-900">{overallStats.totalMembers}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbCash className="h-6 w-6 text-success" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">
                      {t('overallStats.totalPersonalBalance')}
                    </p>
                    <p className="text-xl font-bold text-primary-900">
                      {formatBalanceWithSign(overallStats.totalPersonalBalance, selectedCurrency, i18n.language)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbCash className="h-6 w-6 text-primary-300" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">
                      {t('overallStats.totalContributionBalance')}
                    </p>
                    <p className="text-xl font-bold text-primary-900">
                      {formatBalanceWithSign(
                        overallStats.totalContributionBalance,
                        selectedCurrency,
                        i18n.language,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbCreditCard className="h-6 w-6 text-secondary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">
                      {t('overallStats.totalDirectDebitActive')}
                    </p>
                    <p className="text-xl font-bold text-primary-900">
                      {overallStats.totalDirectDebitActive}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TbShoppingCart className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">
                      {t('overallStats.totalSalesOrders')}
                    </p>
                    <p className="text-xl font-bold text-primary-900">
                      {overallStats.totalSalesOrders}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Updated Table */}
          <FinancialTable
            columns={[
              { id: 'member', label: t('table.member'), align: 'left' },
              { id: 'wallet', label: t('table.wallet') || 'Wallet', align: 'center' },
              { id: 'directDebit', label: t('table.directDebit'), align: 'center' },
              { id: 'salesOrders', label: t('table.salesOrders'), align: 'center' },
            ]}
            rows={balances}
            renderRow={renderRow}
          />
        </>
      )}
    </div>
  );
}

const DEFAULT_BALANCE_STATS = {
  income: 0,
  expenses: 0,
  balance: 0,
} as const;

